import { useQuery } from '@tanstack/react-query'
import { Pencil, Plus, Tags, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { EmptyState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Can } from '@/features/auth/guards'
import { leaveTypesQuery, useDeleteLeaveType } from '@/features/leave/api'
import { LeaveTypeBadge } from '@/features/leave/components/LeaveBadges'
import { LeaveTypeDialog } from '@/features/leave/components/LeaveTypeDialog'
import type { LeaveType } from '@/features/leave/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** English labels (translation keys, ns `leave`); translated at render. */
const appliesLabel = { staff: 'Staff', student: 'Students', both: 'Staff & students' }

export function LeaveTypesPage() {
  const { t } = useTranslation('leave')
  useDocumentTitle('Leave types')
  const list = useQuery(leaveTypesQuery())
  const remove = useDeleteLeaveType()
  const [editing, setEditing] = useState<LeaveType | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<LeaveType | null>(null)

  const actions = (lt: LeaveType) => (
    <Can permission="leave.manage">
      <div className="flex justify-end gap-1">
        <IconButton
          label={t('Edit {{name}}', { name: lt.name })}
          icon={<Pencil />}
          onClick={() => {
            setEditing(lt)
            setFormOpen(true)
          }}
        />
        <IconButton
          label={
            (lt.requestCount ?? 0) > 0
              ? t('{{name}} is in use — deactivate it instead', { name: lt.name })
              : t('Delete {{name}}', { name: lt.name })
          }
          icon={<Trash2 />}
          disabled={(lt.requestCount ?? 0) > 0}
          onClick={() => setDeleting(lt)}
        />
      </div>
    </Can>
  )

  const columns: Column<LeaveType>[] = [
    {
      key: 'name',
      header: t('Leave type'),
      cell: (lt) => <LeaveTypeBadge name={lt.name} colour={lt.colour} size="md" />,
    },
    { key: 'applies', header: t('For'), cell: (lt) => t(appliesLabel[lt.appliesTo]) },
    {
      key: 'max',
      header: t('Days / year'),
      align: 'right',
      cell: (lt) => <span className="tabular">{lt.maxDaysPerYear ?? t('Unlimited')}</span>,
    },
    {
      key: 'rules',
      header: t('Rules'),
      hideBelow: 'md',
      cell: (lt) => (
        <div className="flex flex-wrap gap-1">
          {lt.requiresAttachment ? (
            <Badge size="sm" tone="info">
              {t('Document required')}
            </Badge>
          ) : null}
          {lt.isPaid === true ? (
            <Badge size="sm">{t('Paid')}</Badge>
          ) : lt.isPaid === false ? (
            <Badge size="sm">{t('Unpaid')}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'used',
      header: t('Requests'),
      align: 'right',
      hideBelow: 'lg',
      cell: (lt) => <span className="tabular">{lt.requestCount ?? 0}</span>,
    },
    {
      key: 'status',
      header: t('Status'),
      cell: (lt) => (
        <Badge size="sm" dot tone={lt.active ? 'success' : 'neutral'}>
          {lt.active ? t('Active') : t('Inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: actions,
    },
  ]

  return (
    <>
      <PageHeader
        title={t('Leave types')}
        description={t(
          'Kinds of leave staff and students can request, with yearly allowances per academic year.',
        )}
        breadcrumbs={[
          { label: t('Operations') },
          { label: t('Leave'), to: '/admin/leave' },
          { label: t('Leave types') },
        ]}
        homeHref="/admin"
        actions={
          <Can permission="leave.manage">
            <Button
              leftIcon={<Plus />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('New leave type')}
            </Button>
          </Can>
        }
      />
      <Card>
        <DataTable
          caption={t('Leave types')}
          columns={columns}
          rows={list.data}
          getRowId={(lt) => lt.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          skeletonRows={5}
          mobileCard={(lt) => (
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1 space-y-1">
                <LeaveTypeBadge name={lt.name} colour={lt.colour} size="md" />
                <p className="text-xs text-muted">
                  {t(appliesLabel[lt.appliesTo])} ·{' '}
                  {lt.maxDaysPerYear === null
                    ? t('Unlimited days/year')
                    : t('{{count}} days/year', { count: lt.maxDaysPerYear })}
                  {lt.active ? '' : ` · ${t('Inactive')}`}
                </p>
              </div>
              {actions(lt)}
            </div>
          )}
          empty={
            <EmptyState
              compact
              icon={<Tags />}
              title={t('No leave types yet')}
              description={t('Create types such as Sick leave or Casual leave.')}
            />
          }
        />
      </Card>
      {formOpen ? <LeaveTypeDialog type={editing} onOpenChange={setFormOpen} /> : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => (o ? undefined : setDeleting(null))}
        title={t('Delete leave type?')}
        description={deleting ? t('“{{name}}” will be removed.', { name: deleting.name }) : undefined}
        confirmLabel={t('Delete')}
        loading={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success(t('Leave type deleted'))
              setDeleting(null)
            },
            onError: (e) => toast.error(t('Could not delete'), toApiError(e).message),
          })
        }
      />
    </>
  )
}
